import React from 'react';
import type { DatabaseResponse } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { getDatabaseTypeMeta, DATABASE_STATUS_LABEL } from './databaseTypeMeta';

/**
 * Linha de tabela de uma base de dados (view "Lista" da galeria). Padrão-lista:
 * checkbox (1ª col) + colunas multi-linha (nome+descrição) + menu ••• (última col).
 */
export interface DatabaseGalleryRowProps {
  database: DatabaseResponse;
  selected?: boolean;
  onOpen: (id: string) => void;
  onToggleSelect?: (id: string) => void;
  onSettings?: (id: string) => void;
  onDelete?: (id: string) => void;
}

export const DatabaseGalleryRow: React.FC<DatabaseGalleryRowProps> = ({
  database,
  selected = false,
  onOpen,
  onToggleSelect,
  onSettings,
  onDelete,
}) => {
  const meta = getDatabaseTypeMeta(database.type);
  const count = database.totalDocuments ?? 0;
  const isActive = database.status === 'active';

  const actions: CardAction[] = [
    { label: 'Abrir', icon: 'external-link', onClick: () => onOpen(database.id) },
  ];
  if (onSettings) actions.push({ label: 'Configurações', icon: 'settings', onClick: () => onSettings(database.id) });
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(database.id), variant: 'danger' });

  const formatDate = (s?: string) =>
    s ? new Date(s).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <tr
      className="border-b transition-colors cursor-pointer"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onClick={() => onOpen(database.id)}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`database-row-${database.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11" onClick={(e) => e.stopPropagation()}>
        {onToggleSelect && (
          <button
            type="button"
            onClick={() => onToggleSelect(database.id)}
            className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
            style={{
              backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
              borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
              color: '#ffffff',
            }}
            aria-label={selected ? 'Desmarcar' : 'Selecionar'}
            data-testid={`database-check-${database.id}`}
          >
            {selected && <Icon name="check" className="w-3 h-3" />}
          </button>
        )}
      </td>

      {/* Base: ícone + nome + descrição */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'var(--theme-primary-background)' }}
          >
            <Icon name={meta.icon} className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {database.name}
            </div>
            {database.description && (
              <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
                {database.description}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Tipo */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-card-text)' }}>{meta.label}</td>

      {/* Registros */}
      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>{count}</td>

      {/* Status */}
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
          style={
            isActive
              ? { backgroundColor: 'var(--theme-success-subtle)', color: 'var(--theme-success-fg)' }
              : { backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }
          }
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'currentColor' }} />
          {DATABASE_STATUS_LABEL[database.status] ?? database.status}
        </span>
      </td>

      {/* Atualizado */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>{formatDate(database.updatedAt)}</td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14" onClick={(e) => e.stopPropagation()}>
        <CardActionsMenu actions={actions} size="sm" data-testid={`database-actions-${database.id}`} />
      </td>
    </tr>
  );
};

DatabaseGalleryRow.displayName = 'DatabaseGalleryRow';
